import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  BarChart3, Globe2, DollarSign, Sparkles, User, Clock, AlertCircle, CheckCircle, Zap,
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import CreatorIntelligenceSection from '@/components/analytics/CreatorIntelligenceSection';
import PerformanceAnalyticsDialog from '@/components/analytics/PerformanceAnalyticsDialog';
import PlatformIntelligenceDialog from '@/components/analytics/PlatformIntelligenceDialog';
import RevenueIntelligenceDialog from '@/components/analytics/RevenueIntelligenceDialog';
import GrowthRecommendationsDialog from '@/components/analytics/GrowthRecommendationsDialog';

const API = window.__BACKEND_URL;

function DashboardTile({ icon: Icon, title, summary, onClick, accent, disabled }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className="text-left rounded-xl border border-white/10 bg-white/[0.02] p-4 transition-colors enabled:hover:bg-white/5 enabled:hover:border-blue-400/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3A7BFF]/60 disabled:opacity-40 disabled:cursor-not-allowed"
      data-testid={`dashboard-tile-${title.replace(/\s+/g, '-').toLowerCase()}`}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${accent}`}>
        <Icon className="h-4.5 w-4.5" />
      </div>
      <p className="text-sm font-semibold text-white">{title}</p>
      <p className="text-xs text-[#8A93A6] mt-1">{summary}</p>
    </button>
  );
}

export default function Analytics() {
  const { user } = useContext(AuthContext);
  const [creators, setCreators] = useState([]);
  const [selectedCreator, setSelectedCreator] = useState('all');
  const [connectedPlatforms, setConnectedPlatforms] = useState([]);
  const [creatorInfo, setCreatorInfo] = useState(null);
  const [timeframe, setTimeframe] = useState('30d');
  const [openPanel, setOpenPanel] = useState(null); // 'performance' | 'platform' | 'revenue' | 'growth' | null
  const [intelPrefill, setIntelPrefill] = useState(null);

  // Check if monetization should be visible (Creator+ only, not Starter)
  const showMonetization = user?.plan_tier && !['free', 'starter'].includes(user.plan_tier);

  // Check if user is agency tier (can select creators)
  const isAgency = user?.plan_tier && ['agency', 'agency_elite', 'enterprise'].includes(user.plan_tier);

  useEffect(() => {
    // Fetch creators for agency users
    if (isAgency) {
      fetch(`${API}/api/creators`, { credentials: 'include' })
        .then(res => res.ok ? res.json() : [])
        .then(data => {
          const creatorList = Array.isArray(data) ? data : data?.creators || [];
          setCreators(creatorList);
        })
        .catch(() => setCreators([]));
    }

    // Fetch creator profile to get connected platforms
    fetch(`${API}/api/creator-profile`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          setCreatorInfo(data);
          // Check which platforms are connected based on profile data
          const platforms = [];
          if (data.youtube_connected) platforms.push('youtube');
          if (data.tiktok_connected) platforms.push('tiktok');
          if (data.instagram_connected) platforms.push('instagram');
          if (data.facebook_connected) platforms.push('facebook');
          setConnectedPlatforms(platforms);
        }
      })
      .catch(() => setConnectedPlatforms([]));

    // Also fetch from activeCreatorId for richer data
    const activeId = localStorage.getItem('activeCreatorId');
    if (activeId) {
      fetch(`${API}/api/creators/${activeId}`, { credentials: 'include' })
        .then(res => res.ok ? res.json() : null)
        .then(data => { if (data) setCreatorInfo(prev => ({ ...prev, ...data })); })
        .catch(() => {});
    }
  }, [isAgency]);

  const timeframes = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
    { value: 'all', label: 'All Time' },
  ];

  const actualIncomeTotal = (creatorInfo?.manual_income_entries || []).reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
  const activeCreatorId = localStorage.getItem('activeCreatorId');

  // The only data genuinely isolated per creator today is Creator
  // Intelligence (stored locally, keyed by creator id). Connected
  // platforms / income live on the single account-level creator-profile
  // record in the backend and aren't split per creator yet — selecting a
  // specific creator below does not fabricate separate numbers for those.
  const selectedCreatorRecord = isAgency && selectedCreator !== 'all' ? creators.find((c) => c.id === selectedCreator) : null;
  const effectiveCreatorId = selectedCreatorRecord ? selectedCreatorRecord.id : activeCreatorId;
  const displayName = selectedCreatorRecord?.name || selectedCreatorRecord?.display_name || creatorInfo?.name || creatorInfo?.display_name || 'Creator';
  const displayHandle = selectedCreatorRecord?.handle || creatorInfo?.handle || creatorInfo?.username || '@creator';

  const applyRecommendationToIntel = (cardType, patch) => {
    setOpenPanel(null);
    setIntelPrefill({ cardType, patch });
  };

  return (
    <div className="space-y-6">
      {/* Creator Context */}
      <div className="flex items-center justify-between flex-wrap gap-3" data-testid="analytics-creator-context">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center shrink-0">
            <User className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white uppercase tracking-wide">
              {displayName}
            </p>
            <p className="text-xs text-[#8A93A6]">
              {displayHandle}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1 bg-white/5 rounded-lg p-0.5" data-testid="timeframe-selector">
          {timeframes.map((tf) => (
            <button
              key={tf.value}
              onClick={() => setTimeframe(tf.value)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                timeframe === tf.value ? 'bg-blue-500/20 text-blue-400' : 'text-[#8A93A6] hover:text-white'
              }`}
            >
              {tf.label}
            </button>
          ))}
        </div>
      </div>

      {/* Creator Selector for Agency users — restored original label/position */}
      {isAgency && creators.length > 0 && (
        <div className="flex items-center gap-3" data-testid="analytics-creator-selector-row">
          <label className="text-sm text-[#8A93A6]">Viewing analytics for:</label>
          <Select value={selectedCreator} onValueChange={setSelectedCreator}>
            <SelectTrigger className="w-[200px] bg-[#1C1F26] border-white/10 text-white" data-testid="analytics-creator-selector">
              <SelectValue placeholder="Select creator" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Creators</SelectItem>
              {creators.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name || c.display_name || 'Unnamed'}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {isAgency && selectedCreator === 'all' && creators.length > 0 && (
        <p className="text-xs text-[#555E6E] -mt-4" data-testid="intel-all-creators-note">
          Creator Intelligence shown below is for your primary creator — select an individual creator above to view or edit theirs.
        </p>
      )}

      {/* Creator Intelligence Overview */}
      <CreatorIntelligenceSection creatorId={effectiveCreatorId} prefill={intelPrefill} onPrefillConsumed={() => setIntelPrefill(null)} />

      {/* Smart Analytics Dashboard — 4 more feature areas */}
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">Smart Analytics</h2>
        <p className="text-xs text-[#8A93A6] mb-4">Showing: {timeframe === 'all' ? 'All Time' : timeframes.find(t => t.value === timeframe)?.label}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="smart-analytics-tiles">
          <DashboardTile
            icon={BarChart3} title="Performance Analytics" accent="bg-blue-500/10 text-blue-400"
            summary={connectedPlatforms.length > 0 ? 'Views, watch time, engagement & more' : 'No data yet — connect a platform to track'}
            onClick={() => setOpenPanel('performance')}
          />
          <DashboardTile
            icon={Globe2} title="Platform Intelligence" accent="bg-purple-500/10 text-purple-400"
            summary={`${connectedPlatforms.length}/4 platforms connected`}
            onClick={() => setOpenPanel('platform')}
          />
          <DashboardTile
            icon={DollarSign} title="Revenue Intelligence" accent="bg-green-500/10 text-green-400"
            summary={showMonetization ? (actualIncomeTotal > 0 ? `$${actualIncomeTotal.toFixed(2)} actual income on record` : 'No income entries yet') : 'Upgrade to Creator or Pro to unlock'}
            onClick={() => setOpenPanel('revenue')}
            disabled={!showMonetization}
          />
          <DashboardTile
            icon={Sparkles} title="Growth Recommendations" accent="bg-amber-500/10 text-amber-400"
            summary="6 illustrative suggestions available"
            onClick={() => setOpenPanel('growth')}
          />
        </div>
      </div>

      {/* Plan Gate note for Starter Users */}
      {!showMonetization && user?.plan_tier && (
        <Alert className="bg-pink-500/10 border-pink-500/30">
          <DollarSign className="h-4 w-4 text-pink-500" />
          <AlertDescription className="text-pink-400">
            <strong>Upgrade to Creator or Pro</strong> to unlock Monetization Insights and income estimates.
          </AlertDescription>
        </Alert>
      )}

      {/* Autopilot Activity */}
      <AutopilotActivity />

      <PerformanceAnalyticsDialog open={openPanel === 'performance'} onOpenChange={(v) => setOpenPanel(v ? 'performance' : null)} connectedPlatforms={connectedPlatforms} timeframe={timeframe} />
      <PlatformIntelligenceDialog open={openPanel === 'platform'} onOpenChange={(v) => setOpenPanel(v ? 'platform' : null)} connectedPlatforms={connectedPlatforms} />
      {showMonetization && (
        <RevenueIntelligenceDialog open={openPanel === 'revenue'} onOpenChange={(v) => setOpenPanel(v ? 'revenue' : null)} creatorInfo={creatorInfo} />
      )}
      <GrowthRecommendationsDialog open={openPanel === 'growth'} onOpenChange={(v) => setOpenPanel(v ? 'growth' : null)} onApplyToIntel={applyRecommendationToIntel} />
    </div>
  );
}
function AutopilotActivity() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = window.__BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    loadSubmissions();
  }, []);

  const loadSubmissions = async () => {
    try {
      const res = await fetch(`${API}/autopilot/submissions`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setSubmissions(data.submissions || []);
      }
    } catch (error) {
      console.error('Error loading autopilot submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      submitted: {
        className: 'border-yellow-500 text-yellow-500',
        icon: Clock,
        label: 'Submitted'
      },
      in_progress: {
        className: 'border-blue-500 text-blue-500',
        icon: AlertCircle,
        label: 'In Progress'
      },
      published: {
        className: 'border-green-500 text-green-500',
        icon: CheckCircle,
        label: 'Published'
      }
    };

    const config = statusConfig[status] || statusConfig.submitted;
    const Icon = config.icon;

    return (
      <Badge variant="outline" className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);
  };

  if (loading) {
    return null; // Don't show anything while loading
  }

  if (submissions.length === 0) {
    return null; // Don't show section if no submissions
  }

  return (
    <section>
      <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
        <Zap className="h-5 w-5 text-pink-500" />
        Assisted Autopilot Activity
      </h2>
      <Card className="glass">
        <CardHeader>
          <CardTitle className="text-base">Execution Progress (Read-only)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {submissions.slice(0, 5).map((submission) => (
              <div
                key={submission.id}
                className="flex items-center justify-between p-3 rounded-lg bg-white/5 border border-white/10"
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-white mb-1">
                    {submission.content_title || 'Untitled Content'}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-[#8A93A6]">
                    <span>Updated {formatDate(submission.status_updated_at || submission.submitted_at)}</span>
                    <span>•</span>
                    <div className="flex gap-1">
                      {submission.platforms.slice(0, 2).map((platform) => (
                        <span key={platform} className="capitalize">{platform}</span>
                      ))}
                      {submission.platforms.length > 2 && (
                        <span>+{submission.platforms.length - 2}</span>
                      )}
                    </div>
                  </div>
                </div>
                {getStatusBadge(submission.status)}
              </div>
            ))}
          </div>
          {submissions.length > 5 && (
            <p className="text-xs text-[#8A93A6] text-center mt-4">
              Showing 5 of {submissions.length} submissions. <a href="/app/create/autopilot/submissions" className="text-blue-400 hover:underline">View all</a>
            </p>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
